import { useCallback, useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { webhooksEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { createEntityCache } from '@/hooks/utils/entityCache';
import { getRandomElement } from '@/redux/dialogs/helper';
import { popModal } from '@/redux/modals/actions';
import {
  Webhook,
  PartialWebhook,
  Webhooks,
  PreNbaWebhookTest,
  WebhookTest,
  WebhookTestResultsResponse,
  WebhookType,
} from '@/shared/types/webhook';
import {
  NAME_SUFFIX_BUDGET,
  normalizeForLengthCheck,
  resolveAvailableName,
  truncateToServerLimit,
} from '@/util/checkNames';
import {
  exportWebhook as downloadWebhook,
  importWebhook as uploadWebhook,
} from '@/util/fileManager';
import { LENGTH_S } from '@/util/validation/constants';

const COPY_SUFFIX = ' (Copy)';
// `suggestName` is unbounded, so the resolved name is re-measured below.
const IMPORT_NAME_BASE_LIMIT = LENGTH_S - COPY_SUFFIX.length - NAME_SUFFIX_BUDGET;

const buildCopyName = (sourceName: string, baseLimit: number): string =>
  `${truncateToServerLimit(sourceName, baseLimit)}${COPY_SUFFIX}`;

/**
 * Resolves `name` against check_name. Rethrows a 4xx (create would reject the same name, so
 * falling back would only manufacture a second 400) and swallows network/5xx.
 */
const resolveOrFallback = async (brainId: string, name: string): Promise<string> => {
  try {
    return await resolveAvailableName(endpoints.checkName(brainId, name), name);
  } catch (error) {
    const status = (error as { statusCode?: number } | undefined)?.statusCode;
    if (status !== undefined && status < 500) {
      throw error;
    }
    return name;
  }
};

export const resolveImportedWebhookName = async (
  brainId: string,
  sourceName: string
): Promise<string> => {
  const copyName = buildCopyName(sourceName, IMPORT_NAME_BASE_LIMIT);
  const resolved = await resolveOrFallback(brainId, copyName);

  // A long-enough suffix (` (100)`, or a hand-named ` (500)`) can push the suggestion past the
  // limit. Give back the overflow from the base and re-resolve once.
  const overflow = normalizeForLengthCheck(resolved).length - LENGTH_S;
  if (overflow <= 0) {
    return resolved;
  }
  return resolveOrFallback(brainId, buildCopyName(sourceName, IMPORT_NAME_BASE_LIMIT - overflow));
};

export const API = Object.freeze({
  listWebhooks: async (id: string): Promise<Webhooks> => callGet(endpoints.webhooks(id)),
  getWebhook: async (id: string, wid: string): Promise<Webhook> =>
    callGet(endpoints.webhook(id, wid)),
  createWebhook: async (newWebhook: Partial<Webhook>): Promise<Webhook> =>
    callPost(endpoints.webhooks(newWebhook.brain_id), newWebhook),
  testWebhook: async (
    id: string,
    testData: WebhookTest | PreNbaWebhookTest
  ): Promise<WebhookTestResultsResponse> => callPut(endpoints.test(id), testData),

  updateWebhook: async ({ brain_id, webhook_id, ...webhook }: PartialWebhook): Promise<Webhook> =>
    callPut(endpoints.webhook(brain_id, webhook_id), webhook),

  deleteWebhook: async (webhook: PartialWebhook): Promise<Webhook> =>
    callDelete(endpoints.webhook(webhook.brain_id, webhook.webhook_id)),
});

export const webhookCache = createEntityCache<Webhook, 'webhooks'>({
  listKey: (brainId) => [endpoints.webhooks(brainId)],
  detailKey: (brainId, webhookId) => [endpoints.webhook(brainId, webhookId)],
  listField: 'webhooks',
  getId: (webhook) => webhook.webhook_id,
  getParentId: (webhook) => webhook.brain_id,
});

const useWebhooks = (brainId: string, webhook_id?: string) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { data: webhooks, status: listStatus } = useQuery<Webhooks, Error>({
    queryKey: [endpoints.webhooks(brainId)],
    queryFn: () => API.listWebhooks(brainId),
    enabled: Boolean(brainId),
  });

  const {
    data: webhook,
    isFetching: isWebhookLoading,
    isError: isWebhookError,
    isSuccess: isWebhookSuccess,
  } = useQuery<Webhook, Error>({
    queryKey: [endpoints.webhook(brainId, webhook_id)],
    queryFn: () => API.getWebhook(brainId, webhook_id),
    initialData: webhooks?.webhooks?.find((item) => webhook_id === item.webhook_id),
    enabled: Boolean(brainId) && Boolean(webhook_id),
  });

  const {
    mutate: testWebhook,
    data: testResults,
    status: testStatus,
  } = useMutation<WebhookTestResultsResponse, Error, WebhookTest | PreNbaWebhookTest>({
    mutationFn: (data) => API.testWebhook(brainId, data),
    onSuccess: () => {
      toast.success(t('webhook.test_executed'));
    },
    meta: { errorToast: true },
  });

  const { mutate: updateWebhook, status: updateStatus } = useMutation<
    Webhook,
    Error,
    PartialWebhook
  >({
    mutationFn: API.updateWebhook,
    onSuccess: (resp) => {
      webhookCache.updated(queryClient, resp);
      toast.success(t('webhook.webhook_updated'));
    },
    meta: { errorToast: true },
  });

  const { mutate: createWebhook, status: createStatus } = useMutation<
    Webhook,
    Error,
    Partial<Webhook>
  >({
    mutationFn: API.createWebhook,
    onSuccess: (resp) => {
      webhookCache.created(queryClient, resp);
      toast.success(t('webhook.webhook_created'));
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteWebhook, status: deleteStatus } = useMutation<Webhook, Error, string>({
    mutationFn: (id) => API.deleteWebhook({ brain_id: brainId, webhook_id: id }),
    onSuccess: (resp) => {
      dispatch(popModal());
      webhookCache.removed(queryClient, { parentId: brainId, id: resp.webhook_id });
      toast.success(t('webhook.webhook_deleted'));
    },
    meta: { errorToast: true },
  });

  const allWebhooks = useMemo(() => webhooks?.webhooks ?? [], [webhooks?.webhooks]);

  const actionWebhooks = useMemo(
    () => allWebhooks.filter((item) => item.type === WebhookType.ACTION),
    [allWebhooks]
  );

  const authValidationWebhooks = useMemo(
    () => allWebhooks.filter((item) => item.type === WebhookType.AUTH_VALIDATION),
    [allWebhooks]
  );

  const authPreEnterWebhooks = useMemo(
    () => allWebhooks.filter((item) => item.type === WebhookType.AUTH_PRE_ENTER),
    [allWebhooks]
  );

  const randomWebhook = useMemo(() => getRandomElement(actionWebhooks), [actionWebhooks]);

  const exportWebhook = useCallback(async () => {
    if (!webhook) {
      return;
    }
    downloadWebhook(webhook);
    toast.success(t('webhook.webhook_exported', { 0: webhook.name }));
  }, [webhook, t]);

  const importWebhook = useCallback(
    async (file: File) => {
      try {
        const webhookData = await uploadWebhook(file);

        // Create a new webhook with imported data, excluding fields that should be generated
        const {
          webhook_id: _webhook_id,
          brain_id: _brain_id,
          created: _created,
          updated: _updated,
          ...importedWebhook
        } = webhookData;

        const name = await resolveImportedWebhookName(brainId, importedWebhook.name || 'Imported');

        createWebhook(
          { ...importedWebhook, brain_id: brainId, name },
          {
            onSuccess: () => {
              toast.success(t('webhook.webhook_imported', { 0: name }));
            },
          }
        );
      } catch (error) {
        toast.error(error.message || t('webhook.import_error'));
      }
    },
    [brainId, createWebhook, t]
  );

  return {
    webhooks: allWebhooks,
    listStatus,
    webhook,
    isWebhookLoading,
    isWebhookError,
    isWebhookSuccess,
    createStatus,
    deleteStatus,
    updateStatus,
    testResults,
    testStatus,
    createWebhook,
    deleteWebhook,
    updateWebhook,
    testWebhook,
    actionWebhooks,
    authValidationWebhooks,
    authPreEnterWebhooks,
    randomWebhook,
    exportWebhook,
    importWebhook,
  };
};

export default useWebhooks;
